// ReportByObj.js
import React, { useState, useEffect, useMemo } from 'react';
import { Table, Card, Tag, Space, Typography, List, Row, Col, Breadcrumb, Button, Tooltip, notification, Tabs } from 'antd';
import {
  FileOutlined,
  UserOutlined,
  CalendarOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  PictureOutlined,
} from '@ant-design/icons';
const { Text, Title } = Typography;

import { intialPaginationState, showingPageTotalRange, unexpectedError, dateTime, filter } from '../../../common_functions';

const ReportByObj = props => {
  const { reportByObj } = props;
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState(intialPaginationState);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [sorter, setSorter] = useState(null);
  const [filtersItems, setFiltersItems] = useState({});
  const [activeTabs, setActiveTabs] = useState({});

  const fileCategories = {
    provenance: {
      icon: <FileOutlined className="icon-content-margin" />,
      name: 'Provenance',
      color: 'red',
      tabName: 'Provenance Docs',
    },
    authenticity: {
      icon: <FileOutlined className="icon-content-margin" />,
      name: 'Authenticity',
      color: 'orange',
      tabName: 'Authenticity Files',
    },
    technique: {
      icon: <FileOutlined className="icon-content-margin" />,
      name: 'Technique',
      color: 'gold',
      tabName: 'Technique Docs',
      description: 'Technical documentation about creation methods',
    },
    thumbnail: {
      icon: <PictureOutlined className="icon-content-margin" />,
      name: 'Thumbnail',
      color: 'green',
      tabName: 'Thumbnails',
    },
    xrf: {
      icon: <FileOutlined className="icon-content-margin" />,
      name: 'XRF',
      color: 'volcano',
      tabName: 'XRF Data',
    },
    micrometer: {
      icon: <FileOutlined className="icon-content-margin" />,
      name: 'Micrometer',
      color: 'purple',
      tabName: 'Micrometer Data',
      description: 'Precision thickness measurements',
    },
    glossmeter: {
      icon: <FileOutlined className="icon-content-margin" />,
      name: 'Glossmeter',
      color: 'blue',
      tabName: 'Glossmeter Data',
    },
    spectrometer: {
      icon: <FileOutlined className="icon-content-margin" />,
      name: 'Photospectrometer',
      color: 'cyan',
      tabName: 'Photospectrometer Data',
    },
    basicPhotos: {
      icon: <PictureOutlined className="icon-content-margin" />,
      name: 'Basic Photos',
      color: 'lime',
      tabName: 'Basic Photos',
    },
    mount: {
      icon: <PictureOutlined className="icon-content-margin" />,
      name: 'Mount',
      color: 'geekblue',
      tabName: 'Mount Conditions',
    },
  };

  const FileCard = React.memo(({ file }) => {
    if (!file) return null;
    return (
      <Card hoverable style={{ width: 300, marginBottom: 8 }}>
        <Tooltip title={file.filename_download}>
          <Text strong ellipsis style={{ display: 'block' }}>
            {fileCategories[file.type]?.icon || <FileOutlined className="icon-content-margin" />}
            {file?.filename_download}
          </Text>
        </Tooltip>
        <Space direction="vertical" size={0} style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <UserOutlined className="icon-content-margin" />{' '}
            <span className="text-capitalize">
              {file.uploaded_by?.first_name || 'Anonymous User'} {file.uploaded_by?.last_name || ''}
            </span>
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <CalendarOutlined className="icon-content-margin" /> {dateTime(file.uploaded_on)}
          </Text>
          {file.modified_on && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              <EditOutlined className="icon-content-margin" /> Modified: {dateTime(file.modified_on)}
            </Text>
          )}
        </Space>
      </Card>
    );
  });

  const getGroupedFiles = useMemo(
    () => record => {
      const mapFiles = (key, title, files) => ({
        key,
        title,
        files: files.filter(Boolean).map(file => ({ ...file, type: key })),
      });

      return [
        mapFiles('provenance', 'Provenance Documents', [record.provenance_doc]),
        mapFiles('authenticity', 'Authenticity Files', [record.authenticity_file]),
        mapFiles('technique', 'Technique Documents', [record?.print?.technique_doc]),
        mapFiles('thumbnail', 'Thumbnails', [record?.print?.thumbnail]),
        mapFiles('xrf', 'XRF Data', record?.print?.xrf?.flatMap(x => [x?.xrf, x?.signature_graph]) || []),
        mapFiles('micrometer', 'Micrometer Data', record?.print?.micrometer?.flatMap(m => [m?.photo, m?.mount, m?.bag]) || []),
        mapFiles('glossmeter', 'Glossmeter Data', record?.print?.glossmeter?.map(g => g?.glossmeter) || []),
        mapFiles(
          'spectrometer',
          'Spectrometer Data',
          record?.print?.photo_spectro_meter?.flatMap(s => [
            s?.dmax_oba,
            s?.dmax_lab,
            s?.dmax_spectrum,
            s?.dmin_oba,
            s?.dmin_lab,
            s?.dmin_spectrum,

            s?.light_gray_oba,
            s?.light_gray_lab,
            s?.light_gray_spectrum,

            s?.medium_gray_oba,
            s?.medium_gray_lab,
            s?.medium_gray_spectrum,
          ]) || []
        ),
        mapFiles(
          'basicPhotos',
          'Basic Photos',
          record?.print?.basic_photos?.flatMap(photo => [
            photo?.print_only,
            photo?.print_and_mount_recto,
            photo?.mount_verso,
            photo?.signature_recto,
            photo?.signature_verso,
            ...(photo?.annotations_verso || []).map(a => a.directus_files_id),
            ...(photo?.annotations_recto || []).map(a => a.directus_files_id),
            ...(photo?.stamp_verso || []).map(s => s.directus_files_id),
            ...(photo?.image_detail || []).map(i => i.directus_files_id),
          ]) || []
        ),
        mapFiles(
          'mount',
          'Mount Conditions',
          record?.print?.print_mount_con?.flatMap(m => [m?.edge, m?.mount_branding, m?.verso_texture, m?.recto_texture]) || []
        ),
      ].filter(g => g.files.length);
    },
    []
  );

  const columns = [
    {
      title: 'Print Object ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      fixed: 'left',
    },
    {
      title: 'Collection Name',
      dataIndex: ['collections', 'name'],
      key: 'collections',
      width: 180,
      render: text => <Text style={{ textTransform: 'capitalize' }}>{text}</Text>,
      ...filter('name'),
    },
    {
      title: 'Common Title',
      dataIndex: ['print', 'object_name'],
      key: 'common_title',
      width: 200,
      render: text => <Text style={{ textTransform: 'capitalize' }}>{text}</Text>,
      ...filter('common_title'),
    },
    {
      title: 'Acquisition ID #',
      dataIndex: 'acquisition',
      key: 'acquisition',
      width: 200,
      render: text => <Text style={{ textTransform: 'capitalize' }}>{text}</Text>,
      ...filter('acquisition'),
    },
    {
      title: 'Nominal Size',
      dataIndex: 'recorded_size',
      key: 'recorded_size',
      width: 120,
      ...filter('recorded_size'),
    },
    {
      title: 'Location',
      dataIndex: ['location', 'name'],
      key: 'location',
      width: 150,
      render: text => <Text style={{ textTransform: 'capitalize' }}>{text}</Text>,
      ...filter('name'),
    },
    {
      title: 'Documents',
      key: 'documents',
      width: 180,
      fixed: 'right',
      render: (_, record) => {
        const groups = getGroupedFiles(record);
        const totalFiles = groups.reduce((sum, g) => sum + g.files.length, 0);

        return (
          <Tooltip lacement="top" title="View Files Info">
            <Button
              type="link"
              onClick={e => {
                e.stopPropagation();
                setExpandedRowKeys(prev => (prev.includes(record.id) ? prev.filter(k => k !== record.id) : [...prev, record.id]));
                setActiveTabs(prev => ({ ...prev, [record.id]: prev[record.id] || groups[0]?.key }));
              }}
              style={{ padding: '4px 0' }}
            >
              <Tag color={totalFiles > 0 ? 'blue' : 'default'} className="cap-tag" icon={<EyeOutlined />}>
                {totalFiles} {totalFiles === 1 ? 'File' : 'Files'}
              </Tag>
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  const expandedRowRender = record => {
    const groups = getGroupedFiles(record);
    const activeKey = activeTabs[record.id] || groups[0]?.key || '';

    return (
      <div
        style={{
          background: '#fafafa',
          padding: '0 16px',
          margin: '8px 0',
          // borderRadius: 4,
          // boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        }}
      >
        <Tabs
          activeKey={activeKey}
          onChange={key => setActiveTabs(prev => ({ ...prev, [record.id]: key }))}
          tabPosition="top"
          type="card"
          style={{ marginBottom: 0 }}
          items={groups.map(group => ({
            key: group.key,
            label: (
              <span>
                {fileCategories[group.key]?.tabName || group.title}
                <Tag color={fileCategories[group.key]?.color} style={{ marginLeft: 8 }}>
                  {group.files.length}
                </Tag>
              </span>
            ),
            children: (
              <div style={{ padding: '16px 0', minHeight: 280 }}>
                <List
                  grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 4 }}
                  dataSource={group.files}
                  renderItem={file => (
                    <List.Item>
                      <FileCard file={file} />
                    </List.Item>
                  )}
                  style={{ marginTop: 16 }}
                />
              </div>
            ),
          }))}
        />
      </div>
    );
  };

  useEffect(() => {
    const fetchData = async () => await getColData(pagination);
    fetchData();
  }, []);

  const getColData = async (pagination, filters = {}, sorter = null) => {
    try {
      setLoading(true);
      const response = await props.reportByObj({
        sort: sorter?.field ? `${sorter.order === 'ascend' ? '' : '-'}${sorter.field}` : '-id',
        offset: Number(pagination.pageSize * (pagination.current - 1)) || 0,
        limit: pagination.pageSize,
        meta: 'filter_count',
        filter: filters,
      });
      pagination.total = response.meta.filter_count;
      setPagination({ ...pagination });
      setReports(response.data);
    } catch (error) {
      console.error(error);
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    setPagination(pagination);
    setSorter(sorter);
    const filtersitems = {};
    for (let key in filters) {
      if (filters[key]) {
        if (key === 'collections' || key === 'location') {
          filtersitems[key] = { name: { _icontains: filters[key][0] } };
        } else if (key === 'common_title') {
          filtersitems['print'] = { object_name: { _icontains: filters[key][0] } };
        } else {
          filtersitems[key] = key === 'id' ? { _eq: filters[key][0] } : { _icontains: filters[key][0] };
        }
      }
    }
    setFiltersItems(filtersitems);
    await getColData(pagination, filtersitems, sorter);
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      await reportByObj({ sort: '-id', limit: -1, filter: filtersItems || {} }, true);
    } catch (error) {
      console.log('error :', error);
      notification[error ? 'error' : 'info']({
        message: error ? 'Error occurred while generating CSV' : 'No data available for download',
        duration: 2,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Row className={loading ? 'disabledComponent' : ''}>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Reports", href: "/reports/report-by-object" }, { title: "Report By Object" }]} />
      </Col>
      <Col span={24} className="white-paper">
        <div className="dashboard">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={24} md={12}>
              <Title level={4} style={{ margin: 0 }}>
                Reports
              </Title>
            </Col>
            <Col xs={24} md={12} style={{ textAlign: 'right' }}>
              <Button icon={<DownloadOutlined />} type="primary" loading={loading} style={{ minWidth: 160 }} onClick={handleDownload}>
                Download CSV
              </Button>
            </Col>
          </Row>
          <hr style={{ margin: '16px 0' }} />
          <Table
            columns={columns}
            dataSource={reports}
            rowKey={record => record.id}
            loading={loading}
            scroll={{ x: 'max-content' }}
            expandable={{
              expandedRowRender,
              expandedRowKeys,
              onExpand: (expanded, record) => {
                setExpandedRowKeys(expanded ? [record.id] : []);
                if (expanded) {
                  const groups = getGroupedFiles(record);
                  setActiveTabs(prev => ({
                    ...prev,
                    [record.id]: prev[record.id] || groups[0]?.key,
                  }));
                }
              },
              expandIcon: () => null,
              rowExpandable: record => getGroupedFiles(record).length > 0,
            }}
            pagination={
              reports.length > 0
                ? {
                    ...pagination,
                    ...showingPageTotalRange,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                  }
                : false
            }
            onChange={handleTableChange}
          />
        </div>
      </Col>
    </Row>
  );
};

export default ReportByObj;
